import { useState, useEffect, useCallback, useRef } from 'react';
import taskValidatedItemService from '../services/taskValidatedItemService';
import validationService from '../services/validationService';
import projectService from '../services/projectService';
import useNotification from './useNotification';
import useAuth from './useAuth';

/**
 * Custom hook for handling validation functionality
 * Provides methods to load, save, and delete validations from the database
 *
 * @param {Object} options - Configuration options
 * @param {string} options.conversationId - ID of the conversation
 * @param {string} options.projectId - ID of the project
 * @param {string} options.taskId - ID of the task (e.g., 'security-testing', 'user-stories')
 * @param {Object} options.initialState - Initial validation state object (keys are item IDs, values are boolean)
 * @returns {Object} - Validation state and handlers
 */
const useValidation = ({ conversationId, projectId, taskId, initialState = {} }) => {
  const [validatedItems, setValidatedItems] = useState(initialState);
  const [validatorTypeMap, setValidatorTypeMap] = useState({});  // itemId -> 'internal' | 'external'
  const [validatedByUserMap, setValidatedByUserMap] = useState({});  // itemId -> userId who validated
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserMemberType, setCurrentUserMemberType] = useState(null);
  const [projectMembersRef, setProjectMembersRef] = useState([]);
  const [isAssignedValidator, setIsAssignedValidator] = useState(false);
  const { showNotification } = useNotification();
  const { user } = useAuth();

  // Fetch current user's member type and validator assignment status
  useEffect(() => {
    if (!projectId || !user) return;
    const userId = user.id || user._id;

    const fetchMemberType = async () => {
      try {
        const response = await projectService.getProjectMembers(projectId);
        const members = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        setProjectMembersRef(members);
        const currentMember = members.find(m => String(m.user_id) === String(userId));
        setCurrentUserMemberType(currentMember?.member_type || 'internal');
      } catch (err) {
        setCurrentUserMemberType('internal');
      }
    };

    const fetchValidatorStatus = async () => {
      if (!taskId) return;
      try {
        const validators = await validationService.getTaskValidators(projectId, taskId);
        const allValidators = [...(validators.internal || []), ...(validators.external || [])];
        setIsAssignedValidator(allValidators.some(v => String(v.user_id) === String(userId)));
      } catch (err) {
        setIsAssignedValidator(false);
      }
    };

    fetchMemberType();
    fetchValidatorStatus();
  }, [projectId, taskId, user]);

  /**
   * Load existing validations from the database
   */
  const loadValidations = async () => {
    if (!conversationId || !projectId || !taskId) {
      console.log('Missing required parameters for loadValidations:', { conversationId, projectId, taskId });
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log(`Loading validations for conversation: ${conversationId}, task: ${taskId}`);
      
      // Get all validations for this conversation
      const response = await taskValidatedItemService.getConversationValidatedItems(conversationId);
      
      if (response.success && response.data && response.data.length > 0) {
        // Filter validations for this task
        const itemValidations = response.data.filter(item => 
          item.task_id === taskId && 
          item.project_id === projectId
        );
        
        console.log(`Found ${itemValidations.length} validations for task: ${taskId}`);
        
        // Single record per item — last validator wins
        const validatedMap = { ...initialState };
        const vtMap = {};
        const byUserMap = {};

        itemValidations.forEach(validation => {
          const validId = validation.validated_id;
          // Determine which key in validatedMap to mark as true
          let targetKey = validId;

          if (!(validId in validatedMap)) {
            // Legacy fallback: old records use _v{index} format (e.g. "section-0_v0")
            // Try to match against new _vt{timestamp} keys in validatedMap
            const legacyMatch = validId.match(/^(.+)_v(\d+)$/);
            if (legacyMatch) {
              const baseItemId = legacyMatch[1];
              const matchingKey = Object.keys(validatedMap).find(k =>
                k.startsWith(baseItemId + '_vt')
              );
              if (matchingKey) {
                targetKey = matchingKey;
              }
            }
          }

          validatedMap[targetKey] = true;
          if (validation.validated_by) {
            const validatorId = String(validation.validated_by);
            byUserMap[targetKey] = validatorId;
            // Look up validator's member type for icon color
            const validatorMember = projectMembersRef.find(m => String(m.user_id) === validatorId);
            if (validatorMember) {
              vtMap[targetKey] = validatorMember.member_type || 'internal';
            }
          }
        });

        setValidatedByUserMap(byUserMap);
        setValidatorTypeMap(vtMap);
        setValidatedItems(validatedMap);
        console.log('Loaded validations into state:', Object.keys(validatedMap).filter(key => validatedMap[key]));
      } else {
        console.log('No validations found for this conversation and task');
      }
    } catch (error) {
      console.error('Error loading validations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle toggling validation state for an item
   * @param {string} itemId - ID of the item to validate/unvalidate
   * @param {string} itemName - Optional name/title of the item for notification
   */
  const validatingRef = useRef({});
  const handleValidateItem = async (itemId, itemName = '') => {
    if (!conversationId || !projectId || !taskId) {
      showNotification({
        message: 'Missing required parameters for validation',
        type: 'error'
      });
      return;
    }

    // Prevent double-click / concurrent calls for the same item
    if (validatingRef.current[itemId]) return;
    validatingRef.current[itemId] = true;

    console.log(`Toggling validation for item: ${itemId}, task: ${taskId}`);
    
    try {
      const userId = String(user?.id || user?._id);
      const validatedByUserId = validatedByUserMap[itemId] ? String(validatedByUserMap[itemId]) : null;
      const wasValidatedByCurrentUser = validatedByUserId === userId;
      const isCurrentlyValidated = validatedItems[itemId];

      // Only REMOVE if the same user clicks their own validation
      // If different user clicks → UPDATE (override with their validation, change color)
      // If not validated → CREATE new validation
      const shouldRemove = isCurrentlyValidated && wasValidatedByCurrentUser;
      const shouldSave = !shouldRemove;  // save = create or update

      console.log('Validation click:', { itemId, userId, validatedByUserId, wasValidatedByCurrentUser, shouldRemove, shouldSave });

      if (shouldSave) {
        // Optimistic update — show as validated with current user's color
        setValidatedItems(prev => ({ ...prev, [itemId]: true }));
        setValidatorTypeMap(prev => ({ ...prev, [itemId]: currentUserMemberType }));
        setValidatedByUserMap(prev => ({ ...prev, [itemId]: userId }));
      } else {
        // Optimistic update — remove validation
        setValidatedItems(prev => ({ ...prev, [itemId]: false }));
        setValidatorTypeMap(prev => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
        setValidatedByUserMap(prev => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      }

      const newValidatedState = shouldSave;
      
      // Save validation to or remove from the database
      if (newValidatedState) {
        console.log('Saving validation to database:', {
          projectId,
          taskId,
          conversationId,
          validated_id: itemId
        });
        
        // Clean up item name for audit log — strip version suffix and format
        const cleanItemName = itemName || itemId.replace(/_vt.*$/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        await taskValidatedItemService.saveValidatedItem({
          projectId,
          taskId,
          conversationId,
          validated_id: itemId,
          item_name: cleanItemName
        });
        
        console.log('Validation saved successfully');
        showNotification({
          message: itemName ? `"${itemName}" validated successfully` : 'Item validated successfully',
          type: 'success'
        });

        // Verify against DB before auto-approving tile-level validation.
        // Re-fetch validated items to avoid stale local state (e.g. concurrent validators).
        const allItemKeys = Object.keys(initialState);
        const totalItems = allItemKeys.length;
        if (totalItems > 0 && currentUserMemberType && isAssignedValidator) {
          try {
            const freshResponse = await taskValidatedItemService.getConversationValidatedItems(conversationId);
            const freshItems = (freshResponse.success && freshResponse.data) ? freshResponse.data.filter(
              v => v.task_id === taskId && v.project_id === projectId
            ) : [];

            // Build a map of itemId → validator member_type from DB records
            const freshVtMap = {};
            freshItems.forEach(v => {
              const validatorId = String(v.validated_by);
              const member = projectMembersRef.find(m => String(m.user_id) === validatorId);
              if (member) {
                freshVtMap[v.validated_id] = member.member_type || 'internal';
              }
            });

            // Count items validated by the current user's type (from DB, not local state)
            const validatedByMyType = allItemKeys.filter(key => {
              // Check both exact key and version-prefix match for DB records
              if (freshVtMap[key] === currentUserMemberType) return true;
              // Also check legacy/prefix matches
              return Object.keys(freshVtMap).some(dbKey =>
                dbKey.startsWith(key.split('_vt')[0] + '_vt') && freshVtMap[dbKey] === currentUserMemberType
              );
            }).length;

            if (validatedByMyType === totalItems) {
              await validationService.submitValidation(projectId, taskId, {
                decision: 'approved',
                comment: `All ${totalItems} items validated by ${currentUserMemberType} validator.`
              });
              console.log(`✅ Tile-level ${currentUserMemberType} approval submitted (verified from DB)`);
            } else {
              console.log(`⏳ ${validatedByMyType}/${totalItems} items validated by ${currentUserMemberType} — tile not auto-approved yet`);
            }
          } catch (autoErr) {
            console.log('Auto tile validation check skipped:', autoErr);
          }
        }
      } else {
        // Find and delete ONLY the current user's validation for this item
        console.log('Removing validation from database');
        const currentUserId = String(user?.id || user?._id);
        const validations = await taskValidatedItemService.getConversationValidatedItems(conversationId);

        if (validations.success && validations.data && validations.data.length > 0) {
          const validation = validations.data.find(v =>
            v.project_id === projectId &&
            v.task_id === taskId &&
            v.validated_id === itemId &&
            String(v.validated_by) === currentUserId
          );
          
          if (validation && (validation._id || validation.id)) {
            const validationId = validation._id || validation.id;
            console.log('Found validation to delete:', validationId);
            await taskValidatedItemService.deleteValidatedItem(validationId);
            console.log('Validation deleted successfully');
            showNotification({
              message: itemName ? `"${itemName}" validation removed` : 'Validation removed',
              type: 'error'
            });
          } else {
            console.log('No validation found to delete for item:', itemId);
          }
        }
      }
    } catch (error) {
      console.error('Error validating item:', error);
      showNotification({
        message: 'Failed to update item validation status',
        type: 'error'
      });

      // Revert the UI state in case of error
      setValidatedItems(prev => ({
        ...prev,
        [itemId]: !validatedItems[itemId]
      }));
    } finally {
      validatingRef.current[itemId] = false;
    }
  };

  /**
   * Check if an item is validated
   * @param {string} itemId - ID of the item to check
   * @returns {boolean} - Whether the item is validated
   */
  const isItemValidated = (itemId) => {
    return !!validatedItems[itemId];
  };

  // Load validations when conversation/project/task changes
  useEffect(() => {
    if (conversationId && projectId && taskId) {
      loadValidations();
    }
  }, [conversationId, projectId, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-build validator type map when project members load (for correct icon colors)
  useEffect(() => {
    if (projectMembersRef.length > 0 && Object.keys(validatedByUserMap).length > 0) {
      const vtMap = {};
      Object.entries(validatedByUserMap).forEach(([itemId, validatorId]) => {
        const member = projectMembersRef.find(m => String(m.user_id) === String(validatorId));
        if (member) {
          vtMap[itemId] = member.member_type || 'internal';
        }
      });
      if (Object.keys(vtMap).length > 0) {
        setValidatorTypeMap(vtMap);
      }
    }
  }, [projectMembersRef, validatedByUserMap]);

  return {
    validatedItems,
    isLoading,
    handleValidateItem,
    isItemValidated,
    loadValidations,
    setValidatedItems,
    // For icon color: use the actual validator's type if items are validated, otherwise use current user's type
    currentUserMemberType: (() => {
      const vtValues = Object.values(validatorTypeMap);
      if (vtValues.length > 0) {
        // Items exist — show the validator's type, not the viewer's
        const internalCount = vtValues.filter(v => v === 'internal').length;
        return internalCount >= vtValues.length - internalCount ? 'internal' : 'external';
      }
      return currentUserMemberType;
    })(),
    isAssignedValidator,
    getValidatorType: (itemId) => validatorTypeMap[itemId] || currentUserMemberType || 'internal'
  };
};

export default useValidation;